<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dropdown as VDropdown } from 'floating-vue';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SectionCard from 'dashboard/components-next/section-card/SectionCard.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { accountCurrency } from 'dashboard/helper/currency';
import QuoteRejectModal from './QuoteRejectModal.vue';
import QuoteMarkAcceptedModal from './QuoteMarkAcceptedModal.vue';

const props = defineProps({
  quotes: { type: Array, default: () => [] },
  filter: { type: String, default: 'open' },
  selectedQuoteId: { type: [String, Number], default: null },
  compareQuoteId: { type: [String, Number], default: null },
  totalCount: { type: Number, default: 0 },
  loading: { type: Boolean, default: false },
  collapsed: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
  showRemoveBlock: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:filter',
  'select',
  'create',
  'send',
  'withdraw',
  'accept',
  'reject',
  'edit',
  'revise',
  'archive',
  'toggleCollapse',
  'removeBlock',
]);

const { t } = useI18n();
const { currentAccount } = useAccount();
const showRejectModal = ref(false);
const rejectQuoteId = ref(null);
const rejectSaving = ref(false);
const showMarkAcceptedModal = ref(false);
const markAcceptedQuoteId = ref(null);
const markAcceptedSaving = ref(false);
const showDelete = ref(false);
const deleteQuoteId = ref(null);
const openMenuId = ref(null);

const addQuoteButtonClass =
  'w-full inline-flex items-center justify-center gap-2 h-11 px-4 text-sm font-medium rounded-lg bg-woot-50 text-woot-600 hover:bg-woot-75 dark:bg-woot-900/30 dark:text-woot-300 dark:hover:bg-woot-900/50 transition-colors disabled:opacity-50';

const formatAmount = (value, currency) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || accountCurrency(currentAccount.value),
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const statusLabel = status => {
  if (!status) return '';
  const key = `PLANNING.SHOW.QUOTE_STATUS_${String(status).toUpperCase()}`;
  const translated = t(key);
  return translated === key ? status : translated;
};

const isClosedStatus = status =>
  ['accepted', 'rejected', 'withdrawn', 'expired', 'superseded'].includes(
    status
  );

const isIssuedQuote = quote =>
  quote && ['sent', 'viewed'].includes(quote.status);

const rejectionReason = quote =>
  quote?.status === 'rejected' ? quote.rejection_reason : null;

const managerAcceptanceNote = quote => {
  if (quote?.status !== 'accepted') return null;
  const acceptance = quote.latest_acceptance;
  if (acceptance?.signing_mode !== 'manager') return null;
  return acceptance.acceptance_reason || null;
};

const canEditQuote = quote => quote && quote.status === 'not_sent';

const canReviseQuote = quote => isIssuedQuote(quote);

const canDeleteQuote = quote => quote && quote.status === 'not_sent';

const canManageIssuedQuote = quote => isIssuedQuote(quote);

const canRemindQuote = quote => isIssuedQuote(quote);

const primaryAction = quote => {
  if (props.readOnly || !quote) return null;
  if (quote.status === 'not_sent') {
    return {
      key: 'send',
      label: t('PLANNING.SHOW.SEND_QUOTE'),
      icon: 'i-lucide-send',
    };
  }
  if (canRemindQuote(quote)) {
    return {
      key: 'remind',
      label: t('PLANNING.SHOW.REMIND_QUOTE'),
      icon: 'i-lucide-bell',
    };
  }
  return null;
};

const versionLabel = version =>
  t('PLANNING.SHOW.QUOTE_VERSION_SHORT', { version });

const quotePreviewUrl = quote => quote.preview_url || null;

const quoteDownloadUrl = quote => quote.download_url || null;

const quotePeppolDownloadUrl = quote => quote.peppol_download_url || null;

const openQuotePreview = quote => {
  const url = quotePreviewUrl(quote);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
};

const downloadQuotePdf = quote => {
  const url = quoteDownloadUrl(quote);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
};

const downloadQuotePeppol = quote => {
  const url = quotePeppolDownloadUrl(quote);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
};

const quoteTitle = quote => quote.name || t('PLANNING.SHOW.QUOTE_DETAIL_TITLE');

const discountAmount = quote => {
  const fromBreakdown = Number(quote.breakdown?.discount_amount || 0);
  if (fromBreakdown > 0) return fromBreakdown;
  return (quote.breakdown?.line_items || [])
    .filter(l => l.is_discount)
    .reduce((sum, l) => sum + Math.abs(Number(l.line_total || 0)), 0);
};

const discountTaxAmount = quote =>
  (quote.breakdown?.line_items || [])
    .filter(l => l.is_discount)
    .reduce((sum, l) => sum + Math.abs(Number(l.tax_amount || 0)), 0);

const originalTotal = quote => {
  const discount = discountAmount(quote);
  if (discount <= 0) return null;
  return (
    Number(quote.total_incl_tax || 0) + discount + discountTaxAmount(quote)
  );
};

const rejectTarget = computed(
  () => props.quotes.find(q => q.id === rejectQuoteId.value) || null
);

const markAcceptedTarget = computed(
  () => props.quotes.find(q => q.id === markAcceptedQuoteId.value) || null
);

const deleteTarget = computed(
  () => props.quotes.find(q => q.id === deleteQuoteId.value) || null
);

watch(
  () => props.selectedQuoteId,
  () => {
    openMenuId.value = null;
  }
);

const menuActions = computed(() => {
  if (!props.showRemoveBlock) return [];
  return [
    {
      key: 'remove-block',
      label: t('PLANNING.SHOW.REMOVE_BLOCK'),
      icon: 'i-lucide-trash-2',
      danger: true,
    },
  ];
});

const setMenuOpen = (quoteId, shown) => {
  openMenuId.value = shown ? quoteId : null;
};

const handlePrimaryAction = (quote, key) => {
  if (key === 'send' || key === 'remind') emit('send', quote.id);
};

const handleRowMenu = (quote, key) => {
  openMenuId.value = null;
  if (key === 'view') {
    openQuotePreview(quote);
    return;
  }
  if (key === 'download') {
    downloadQuotePdf(quote);
    return;
  }
  if (key === 'download_peppol') {
    downloadQuotePeppol(quote);
    return;
  }
  if (key === 'edit') emit('edit', quote.id);
  if (key === 'revise') emit('revise', quote.id);
  if (key === 'withdraw') emit('withdraw', quote.id);
  if (key === 'accept') {
    emit('select', quote.id);
    markAcceptedQuoteId.value = quote.id;
    showMarkAcceptedModal.value = true;
    showRejectModal.value = false;
    showDelete.value = false;
    return;
  }
  if (key === 'archive') {
    emit('select', quote.id);
    deleteQuoteId.value = quote.id;
    showDelete.value = true;
    showRejectModal.value = false;
    showMarkAcceptedModal.value = false;
    return;
  }
  if (key === 'reject') {
    emit('select', quote.id);
    rejectQuoteId.value = quote.id;
    showRejectModal.value = true;
    showDelete.value = false;
    showMarkAcceptedModal.value = false;
  }
};

const cancelReject = () => {
  showRejectModal.value = false;
  rejectQuoteId.value = null;
  rejectSaving.value = false;
};

const confirmReject = reason => {
  if (!rejectTarget.value || !reason?.trim()) return;
  rejectSaving.value = true;
  emit('reject', rejectTarget.value.id, reason.trim());
  cancelReject();
};

const cancelMarkAccepted = () => {
  showMarkAcceptedModal.value = false;
  markAcceptedQuoteId.value = null;
  markAcceptedSaving.value = false;
};

const confirmMarkAccepted = reason => {
  if (!markAcceptedTarget.value || !reason?.trim()) return;
  markAcceptedSaving.value = true;
  emit('accept', markAcceptedTarget.value.id, reason.trim());
  cancelMarkAccepted();
};

const cancelDelete = () => {
  showDelete.value = false;
  deleteQuoteId.value = null;
};

const confirmDelete = () => {
  if (!deleteTarget.value) return;
  emit('archive', deleteTarget.value.id);
  cancelDelete();
};

const rowBorderClass = quote => {
  if (quote.id === props.selectedQuoteId) {
    return 'border-woot-400 ring-2 ring-woot-200 dark:ring-woot-900/40';
  }
  if (quote.id === props.compareQuoteId) return 'border-n-slate-6';
  return 'border-n-weak';
};
</script>

<template>
  <SectionCard
    :title="t('PLANNING.SHOW.QUOTES_TITLE')"
    icon="i-lucide-file-text"
    collapsible
    collapse-in-menu
    :collapse-label="t('PLANNING.SHOW.COLLAPSE')"
    :expand-label="t('PLANNING.SHOW.EXPAND')"
    :menu-actions="menuActions"
    flush
    :collapsed="collapsed"
    @toggle-collapse="emit('toggleCollapse')"
    @menu-action="action => action === 'remove-block' && emit('removeBlock')"
  >
    <div v-if="loading && !totalCount" class="py-6 text-sm text-n-slate-11">
      {{ t('PLANNING.SHOW.LOADING') }}
    </div>

    <div v-else-if="totalCount === 0" class="py-8 text-center pr-12">
      <Icon
        icon="i-lucide-file-text"
        class="size-12 mx-auto text-woot-75 mb-2"
      />
      <p class="text-sm text-slate-500 font-semibold">
        {{ t('PLANNING.SHOW.NO_QUOTES') }}
      </p>
      <button
        v-if="!readOnly"
        type="button"
        class="mt-4 mx-auto max-w-xs print:hidden"
        :class="addQuoteButtonClass"
        @click="emit('create')"
      >
        <Icon icon="i-lucide-plus" class="size-4" />
        {{ t('PLANNING.SHOW.NEW_QUOTE') }}
      </button>
    </div>

    <div v-else class="min-w-0 pt-1">
      <div class="mb-3 flex flex-wrap items-center gap-1">
        <button
          v-for="f in ['open', 'closed', 'all']"
          :key="f"
          type="button"
          class="rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors"
          :class="
            filter === f
              ? 'bg-woot-50 text-woot-600 dark:bg-woot-900/30 dark:text-woot-300'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="emit('update:filter', f)"
        >
          {{ t(`PLANNING.SHOW.QUOTES_FILTER_${f.toUpperCase()}`) }}
        </button>
      </div>

      <div v-if="!quotes.length" class="py-6 text-sm text-n-slate-11">
        {{ t('PLANNING.SHOW.NO_QUOTES') }}
      </div>

      <div v-else class="flex flex-col gap-2 p-1">
        <div
          v-for="quote in quotes"
          :key="quote.id"
          class="rounded-lg border bg-white dark:bg-slate-950 transition-colors"
          :class="rowBorderClass(quote)"
        >
          <div
            role="button"
            tabindex="0"
            class="flex w-full min-w-0 items-center gap-3 px-3 py-3 text-left cursor-pointer"
            @click="emit('select', quote.id)"
            @keydown.enter.prevent="emit('select', quote.id)"
            @keydown.space.prevent="emit('select', quote.id)"
          >
            <div
              class="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5"
            >
              <div class="flex min-w-0 items-center gap-2">
                <span
                  class="min-w-0 truncate text-sm font-semibold text-n-slate-12"
                >
                  {{ quoteTitle(quote) }}
                </span>
                <span
                  class="shrink-0 text-[0.65rem] font-bold uppercase tracking-wide text-n-slate-11"
                >
                  {{ versionLabel(quote.version) }}
                </span>
                <span
                  class="inline-flex shrink-0 rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold"
                  :class="
                    isClosedStatus(quote.status)
                      ? 'bg-n-slate-3 text-n-slate-11'
                      : 'bg-woot-50 text-woot-600 dark:bg-woot-900/30 dark:text-woot-300'
                  "
                >
                  {{ statusLabel(quote.status) }}
                </span>
              </div>

              <div
                class="flex shrink-0 items-center justify-end gap-1.5 tabular-nums"
              >
                <span
                  v-if="originalTotal(quote)"
                  class="text-xs text-n-slate-10 line-through"
                >
                  {{ formatAmount(originalTotal(quote), quote.currency) }}
                </span>
                <span
                  class="text-sm font-semibold text-woot-600 dark:text-woot-300"
                >
                  {{ formatAmount(quote.total_incl_tax, quote.currency) }}
                </span>
              </div>

              <p
                v-if="rejectionReason(quote)"
                class="min-w-0 truncate text-[11px] text-n-slate-11"
              >
                {{ rejectionReason(quote) }}
              </p>
              <p
                v-else-if="managerAcceptanceNote(quote)"
                class="min-w-0 truncate text-[11px] text-n-slate-11"
                :title="
                  t('PLANNING.SHOW.QUOTE_MARKED_ACCEPTED_NOTE', {
                    reason: managerAcceptanceNote(quote),
                  })
                "
              >
                {{
                  t('PLANNING.SHOW.QUOTE_MARKED_ACCEPTED_NOTE', {
                    reason: managerAcceptanceNote(quote),
                  })
                }}
              </p>
              <span
                class="text-[11px] text-n-slate-11 tabular-nums"
                :class="{
                  'col-start-2':
                    !rejectionReason(quote) && !managerAcceptanceNote(quote),
                }"
              >
                {{ formatAmount(quote.total_excl_tax, quote.currency) }}
                {{ t('PLANNING.SHOW.EXCL_VAT') }}
              </span>
            </div>

            <div
              class="flex shrink-0 items-center gap-1 print:hidden"
              @click.stop
              @mousedown.stop
            >
              <Button
                v-if="primaryAction(quote)"
                size="sm"
                color="blue"
                type="button"
                class="justify-center px-3"
                :icon="primaryAction(quote).icon"
                :label="primaryAction(quote).label"
                @click="handlePrimaryAction(quote, primaryAction(quote).key)"
              />

              <VDropdown
                :shown="openMenuId === quote.id"
                :triggers="['click']"
                placement="bottom-end"
                :distance="8"
                :arrow="false"
                popper-class="event-menu-popper"
                @update:shown="shown => setMenuOpen(quote.id, shown)"
              >
                <button
                  type="button"
                  class="flex items-center justify-center size-8 rounded text-n-slate-10 hover:bg-n-slate-3 hover:text-n-slate-12"
                  :aria-label="t('PLANNING.ALLOCATIONS.MORE_OPTIONS')"
                >
                  <Icon icon="i-lucide-ellipsis" class="size-5 shrink-0" />
                </button>
                <template #popper>
                  <div
                    class="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-75 dark:border-slate-800 p-1.5 flex flex-col min-w-44"
                    @click.stop
                    @mousedown.stop
                  >
                    <button
                      v-if="quotePreviewUrl(quote)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(quote, 'view')"
                    >
                      <Icon
                        icon="i-lucide-eye"
                        class="size-5 text-n-slate-11"
                      />
                      <span>{{ t('PLANNING.SHOW.VIEW_QUOTE_PREVIEW') }}</span>
                    </button>
                    <button
                      v-if="quoteDownloadUrl(quote)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(quote, 'download')"
                    >
                      <Icon
                        icon="i-lucide-download"
                        class="size-5 text-n-slate-11"
                      />
                      <span>{{ t('PLANNING.SHOW.DOWNLOAD_QUOTE') }}</span>
                    </button>
                    <button
                      v-if="quotePeppolDownloadUrl(quote)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(quote, 'download_peppol')"
                    >
                      <Icon
                        icon="i-lucide-file-code-2"
                        class="size-5 text-n-slate-11"
                      />
                      <span>{{
                        t('PLANNING.SHOW.DOWNLOAD_QUOTE_PEPPOL')
                      }}</span>
                    </button>
                    <button
                      v-if="!readOnly && canEditQuote(quote)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(quote, 'edit')"
                    >
                      <Icon
                        icon="i-lucide-pencil"
                        class="size-4 text-n-slate-11"
                      />
                      <span>{{ t('PLANNING.SHOW.EDIT_QUOTE') }}</span>
                    </button>
                    <button
                      v-if="!readOnly && canReviseQuote(quote)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(quote, 'revise')"
                    >
                      <Icon
                        icon="i-lucide-pencil"
                        class="size-5 text-n-slate-11"
                      />
                      <span>{{ t('PLANNING.SHOW.REVISE_QUOTE') }}</span>
                    </button>
                    <button
                      v-if="!readOnly && canManageIssuedQuote(quote)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(quote, 'withdraw')"
                    >
                      <Icon
                        icon="i-lucide-undo-2"
                        class="size-5 text-n-slate-11"
                      />
                      <span>{{ t('PLANNING.SHOW.WITHDRAW_QUOTE') }}</span>
                    </button>
                    <button
                      v-if="!readOnly && canManageIssuedQuote(quote)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2.5 text-n-slate-12"
                      @click="handleRowMenu(quote, 'accept')"
                    >
                      <Icon
                        icon="i-lucide-check"
                        class="size-5 text-n-slate-11"
                      />
                      <span>{{ t('PLANNING.SHOW.MARK_ACCEPTED') }}</span>
                    </button>
                    <button
                      v-if="!readOnly && canManageIssuedQuote(quote)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2.5 text-red-600"
                      @click="handleRowMenu(quote, 'reject')"
                    >
                      <Icon icon="i-lucide-x" class="size-5" />
                      <span>{{ t('PLANNING.SHOW.REJECT_QUOTE') }}</span>
                    </button>
                    <button
                      v-if="!readOnly && canDeleteQuote(quote)"
                      type="button"
                      class="w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2.5 text-red-600"
                      @click="handleRowMenu(quote, 'archive')"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-4" />
                      <span>{{ t('PLANNING.SHOW.DELETE_QUOTE') }}</span>
                    </button>
                  </div>
                </template>
              </VDropdown>
            </div>
          </div>

          <div
            v-if="showDelete && deleteQuoteId === quote.id"
            class="border-t border-n-weak px-3 py-3 print:hidden"
            @click.stop
            @mousedown.stop
          >
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('PLANNING.SHOW.DELETE_QUOTE_CONFIRM') }}
            </p>
            <div class="mt-2 flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="faded"
                color="slate"
                type="button"
                :label="t('PLANNING.EVENTS.CANCEL')"
                @click="cancelDelete"
              />
              <Button
                size="sm"
                color="ruby"
                type="button"
                :label="t('PLANNING.SHOW.DELETE_QUOTE')"
                @click="confirmDelete"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="!readOnly" class="pt-3 print:hidden">
        <button
          type="button"
          :class="addQuoteButtonClass"
          @click="emit('create')"
        >
          <Icon icon="i-lucide-plus" class="size-4" />
          {{ t('PLANNING.SHOW.NEW_QUOTE') }}
        </button>
      </div>
    </div>
  </SectionCard>

  <QuoteRejectModal
    v-if="showRejectModal"
    v-model="showRejectModal"
    :quote="rejectTarget"
    :is-loading="rejectSaving"
    @confirm="confirmReject"
    @cancel="cancelReject"
  />

  <QuoteMarkAcceptedModal
    v-if="showMarkAcceptedModal"
    v-model="showMarkAcceptedModal"
    :quote="markAcceptedTarget"
    :is-loading="markAcceptedSaving"
    @confirm="confirmMarkAccepted"
    @cancel="cancelMarkAccepted"
  />
</template>
